import { useNavigate } from "react-router-dom";

import { type Currency } from "~/api/products/types";
import { ProductAvailabilityBadge } from "~/components/kiosk/products/product-availability-badge";
import { ProductCard } from "~/components/kiosk/products/product-card";
import { useKioskProductQuantity } from "~/stores/kiosk-session/hooks/use-kiosk-product-basket";
import { getIsAddDisabled } from "~/stores/kiosk-session/hooks/use-kiosk-products/get-is-add-disabled";
import { type KioskDisplayProductList } from "~/stores/kiosk-session/hooks/use-kiosk-products/types";
import { priceFormatter } from "~/utils/price-formatter";
import { useKioskStore } from "~/routes/__app.kiosk/route";

import { getProductDetailPath } from "./get-product-detail-path";
import { ProductQuantityControl } from "./product-quantity-control";

type KioskProductListCardProps = {
  item: KioskDisplayProductList;
  currency: Currency;
  locale: string;
};

export const KioskProductListCard = ({
  item,
  currency,
  locale
}: KioskProductListCardProps) => {
  const navigate = useNavigate();
  const hidePrices = useKioskStore(
    (state) => state.till.moduleConfig.hidePrices
  );
  const {
    product,
    unitDisplayPrice,
    unitDisplayListPrice,
    allowance,
    discounts
  } = item;

  const discountId = discounts.find(
    (discount) => discount.type === "stamp-card"
  )?.id;

  const quantity = useKioskProductQuantity(product.id);

  // Gray out / disable the whole card only when the product is unavailable AND
  // not in the basket. While it's in the basket (e.g. stock or user limit
  // reached) the card stays interactive so the shopper can still open detail
  // and use the stepper to remove items - the "+" is disabled by the control
  // itself. Mirrors ProductQuantityControl's own hide condition.
  const isCardDisabled = quantity === 0 && getIsAddDisabled(item);

  return (
    <ProductCard
      id={product.id}
      name={product.name}
      displayPrice={
        hidePrices
          ? undefined
          : priceFormatter({
              value: unitDisplayPrice,
              currency,
              locale
            })
      }
      displayPriceOriginal={
        unitDisplayListPrice && !hidePrices
          ? priceFormatter({
              value: unitDisplayListPrice,
              currency,
              locale
            })
          : undefined
      }
      allowance={allowance}
      tags={product.tags}
      image={product.imageUrl}
      stamps={product.stamps}
      onItemClick={() => {
        navigate(getProductDetailPath(product.id, discountId));
      }}
      stampcardRuleId={discountId}
      disabled={isCardDisabled}
      badge={
        <ProductAvailabilityBadge
          stockStatus={product.stockStatus}
          isLimitOver={product.isLimitOver}
          remaining={product.remaining}
          size="lg"
        />
      }
      quantityControl={
        <ProductQuantityControl item={item} discountId={discountId} />
      }
    />
  );
};
